import React from "react";
import { useAlignedRowInsets } from "../hooks/useAlignedRowInsets";
import { MdKeyboardArrowDown } from "react-icons/md";
import { MobileCartIcon } from "./MobileCartIcon";
import type { StickySummaryToggleTrigger } from "@/shared/utils/bookingAnalytics";

interface SummaryHeaderProps {
  isTotalVisible: boolean;
  totalAmount: number;
  totalAmountDisplay?: string;
  generatedBgColor: string;
  onToggle: (trigger: Extract<StickySummaryToggleTrigger, "cart_icon" | "chevron">) => void;
  // Mobile navigation props
  previousInfo?: {
    pageName: string;
    pagePath: string;
  };
  nextInfo?: {
    pageName: string;
    isActive?: boolean;
  };
  onPreviousClick?: () => void;
  onNextClick?: () => void;
  isAddingToCart?: boolean;
  productRenderedLocation?: string;
  checkoutUrl?: string;
  selectedTrack?: string;
  showChevron?: boolean;
  isAnyItemsInCart?: boolean;
  showMobileCheckout?: boolean;
  hasPendingSelections?: boolean;
  onCheckout?: () => void;
  /** Matches experience selector: HK purple Next, transparent sticky bar on mobile. */
  mobileNavVariant?: "default" | "experience";
  /** When false, hides mobile Next (e.g. add-another-attraction flow with nothing new selected). */
  showMobileNextButton?: boolean;
  /** When false, hides the mobile ($total) Cart expand toggle (e.g. on /). Only affects `desktop:tw-hidden` UI. */
  showMobileCartToggle?: boolean;
  /** Standalone footer (no chevron, e.g. age-group step): keep the cart/bag toggle visible on the right even while the Next button is gated on selection. */
  showCartToggleInStandalone?: boolean;
  /** Total quantity of items currently in the cart (shown as a badge on the mobile cart icon). */
  cartItemCount?: number;
  /** When false, hides the down chevron on the mobile Checkout pill (e.g. on / where there is no cart to expand). Does NOT affect Next. */
  showCheckoutChevron?: boolean;
  /** Overrides the default HK gradient background on the action pill (Checkout / Next). BattleKart still wins. Used on / for the ProgressiveBar gradient. */
  actionPillBgOverride?: string;
  /** Footer = bottom bar with Next/cart. Popup = expanded summary header row (Total on the right). */
  headerContext?: "footer" | "popup";
  /** Date-time step: the Next label carries the session count, e.g. "Next (2/3)". */
  isDateTimeStep?: boolean;
  /** Tile container to line the Next/Checkout pill's edges up with on phones (see useAlignedRowInsets). */
  actionAlignRef?: React.RefObject<HTMLElement>;
  viewSummaryLabel?: string;
  hideSummaryLabel?: string;
}

/** The HK primary-action gradient, for surfaces that cannot take the booking-cta-pill class (e.g. gradient borders). */
export const HK_GRADIENT_BG = "tw-bg-[linear-gradient(270deg,#F908FF_0%,#D32EFE_8.19%,#37CCFB_87.88%,#05FFFA_100%)]";

/** Compact Next/Checkout pill styling for the sticky summary header. */
const EXPERIENCE_NEXT_BUTTON_BASE_CLASS =
  "tw-flex tw-items-center tw-justify-center tw-w-full tw-gap-[2px] tw-py-[8px] tw-font-bold tw-text-[14px] tw-rounded-[100px] tw-text-white tw-transition-opacity tw-duration-200 hover:tw-opacity-90";

/** The label fading out of a Checkout ↔ Next crossfade: out of flow, centred over the visible one. */
const CROSSFADE_HIDDEN_LABEL_CLASS = "tw-absolute tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-opacity-0";

const ACTION_LABEL_CROSSFADE_CLASS =
  "tw-transition-opacity tw-duration-[600ms] tw-ease-[cubic-bezier(0.16,1,0.3,1)]";

const ACTION_CHEVRON_REVEAL_CLASS =
  "tw-inline-flex tw-shrink-0 tw-overflow-hidden tw-transition-[opacity,max-width] tw-duration-[600ms] tw-ease-[cubic-bezier(0.16,1,0.3,1)]";

/** Checkout: match Next left padding; slightly less right padding plus chevron tuck. */
const EXPERIENCE_NEXT_BUTTON_PADDING_CHECKOUT = "tw-pl-[22px] tw-pr-[12px]";
/** Next with chevron: extra left padding, slightly less right for the arrow. */
const EXPERIENCE_NEXT_BUTTON_PADDING_NEXT = "tw-pl-[22px] tw-pr-[16px]";
/** No chevron: symmetric horizontal padding. */
const EXPERIENCE_NEXT_BUTTON_PADDING_NO_CHEVRON = "tw-px-[28px]";

const EXPERIENCE_CHEVRON_CLASS = "tw-shrink-0 tw-text-[32px] tw-leading-none tw-text-white";

const COMPACT_NEXT_PILL_BASE_CLASS =
  "tw-text-white tw-font-[700] tw-rounded-full tw-inline-flex tw-w-full tw-items-center tw-gap-[2px] tw-text-[12px] tw-justify-center tw-h-[44px]";

const COMPACT_NEXT_PILL_PADDING_CHECKOUT = "tw-pl-[20px] tw-pr-[10px]";
const COMPACT_NEXT_PILL_PADDING_NEXT = "tw-pl-[20px] tw-pr-[16px]";

const COMPACT_CHEVRON_CLASS = "tw-shrink-0 tw-text-[28px] tw-leading-none tw-text-white";

const MobileActionChevronDown = ({ variant, isCheckout }: { variant: "experience" | "compact"; isCheckout: boolean }) => (
  <MdKeyboardArrowDown className={`${variant === "experience" ? EXPERIENCE_CHEVRON_CLASS : COMPACT_CHEVRON_CLASS}${isCheckout ? " tw-translate-x-[6px]" : ""}`} aria-hidden />
);

const MOBILE_SUMMARY_OPEN_TRANSITION = "tw-transition-[right,opacity,transform] tw-duration-[450ms] tw-ease-[cubic-bezier(0.16,1,0.3,1)]";
const MOBILE_SUMMARY_CLOSE_TRANSITION = "tw-transition-[right,opacity,transform] tw-duration-[520ms] tw-ease-[cubic-bezier(0.4,0,0.9,1)]";

/** Mobile footer rows: the action pill fills the row; the cart toggle (when shown) sits on its right. */
const MOBILE_FOOTER_ROW_CLASS = "desktop:tw-hidden tw-flex tw-w-full tw-items-end tw-gap-[12px] tw-px-[8px]";

/** Mobile footer action pill height — cart icon + badge overhang align to this. */
const MOBILE_ACTION_PILL_HEIGHT = 44;

export const SummaryHeader: React.FC<SummaryHeaderProps> = React.memo(
  ({
    isTotalVisible,
    totalAmount,
    totalAmountDisplay,
    generatedBgColor,
    onToggle,
    previousInfo,
    nextInfo,
    onPreviousClick,
    onNextClick,
    isAddingToCart,
    productRenderedLocation,
    checkoutUrl,
    selectedTrack,
    showChevron = true,
    isAnyItemsInCart = false,
    showMobileCheckout = true,
    hasPendingSelections = false,
    onCheckout,
    mobileNavVariant = "default",
    showMobileNextButton = true,
    showMobileCartToggle = true,
    showCartToggleInStandalone = false,
    showCheckoutChevron = true,
    actionPillBgOverride,
    cartItemCount = 0,
    headerContext = "footer",
    isDateTimeStep = false,
    actionAlignRef,
    viewSummaryLabel = "View Summary",
    hideSummaryLabel = "Hide Summary",
  }) => {
    const isPopupHeader = headerContext === "popup";
    // On phones, footer rows take the tiles' edges as their side padding when a screen asks for it.
    const { rowRef: alignedRowRef, insetStyle: alignedRowStyle } = useAlignedRowInsets(actionAlignRef);
    const isExperienceNav = mobileNavVariant === "experience";
    const formattedTotalAmount =
      totalAmountDisplay ??
      (() => {
        const rounded = Math.round(totalAmount * 100) / 100;
        return Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(2);
      })();

    // Next/Checkout use theme CTA styling (booking-cta-pill / CSS vars) when provided.
    const isBattleKart = selectedTrack === "BATTLE_KART_TRACK";
    const battleKartNextColor = "booking-cta-pill";
    const mobileBgColor = actionPillBgOverride ?? (isBattleKart ? battleKartNextColor : "booking-cta-pill");
    // Keeps the desktop accent (no --expanded modifier) so the panel header reads as a
    // coloured band above the grey summary body.
    const stickyHeaderClass = isTotalVisible ? `${generatedBgColor} tw-sticky tw-top-0 tw-z-10` : "";

    const showCheckoutAction = isAnyItemsInCart && showMobileCheckout && !hasPendingSelections;

    const mobileSummaryTransition = isTotalVisible ? MOBILE_SUMMARY_OPEN_TRANSITION : MOBILE_SUMMARY_CLOSE_TRANSITION;
    const desktopChevronTransition = isTotalVisible
      ? "tw-transition-transform tw-duration-[450ms] tw-ease-[cubic-bezier(0.16,1,0.3,1)]"
      : "tw-transition-transform tw-duration-[520ms] tw-ease-[cubic-bezier(0.4,0,0.9,1)]";

    const handleToggleClick = (e: React.MouseEvent, trigger: "cart_icon" | "chevron") => {
      e.stopPropagation();
      onToggle(trigger);
    };

    const handleNavClick = (e: React.MouseEvent<HTMLButtonElement>, action?: () => void) => {
      e.stopPropagation();
      if (action) {
        action();
      }
    };

    // Mobile matches desktop on the date/time step: "Next (2/3)" counts the sessions picked so far.
    const checkoutLabel = isAddingToCart ? "Adding..." : "Checkout";
    const nextLabel = isAddingToCart ? "Adding..." : isDateTimeStep && nextInfo?.pageName ? nextInfo.pageName : "Next";

    /** Checkout ↔ Next crossfade. Only the visible label takes up space, so the chevron sits right
        beside it; the fading-out label is overlaid on it. */
    const renderCrossfadedLabel = (showCheckout: boolean) => (
      <span className="tw-relative tw-inline-flex tw-h-[16px] tw-items-center tw-whitespace-nowrap">
        <span
          className={`tw-leading-none ${ACTION_LABEL_CROSSFADE_CLASS} ${showCheckout ? "tw-opacity-100" : CROSSFADE_HIDDEN_LABEL_CLASS}`}
          aria-hidden={!showCheckout}
        >
          {checkoutLabel}
        </span>
        <span
          className={`tw-leading-none ${ACTION_LABEL_CROSSFADE_CLASS} ${showCheckout ? CROSSFADE_HIDDEN_LABEL_CLASS : "tw-opacity-100"}`}
          aria-hidden={showCheckout}
        >
          {nextLabel}
        </span>
      </span>
    );

    /** Fixed 44px height (cart 40px + 4px badge overhang, so the pill spans badge top → cart bottom) so Checkout ↔ Next does not change footer height. */
    const renderExperienceActionButton = (onClick?: () => void, withChevron = true) => {
      const showVisibleChevron = withChevron && (!showCheckoutAction || showCheckoutChevron);
      const paddingClass = !showVisibleChevron
        ? EXPERIENCE_NEXT_BUTTON_PADDING_NO_CHEVRON
        : showCheckoutAction
          ? EXPERIENCE_NEXT_BUTTON_PADDING_CHECKOUT
          : EXPERIENCE_NEXT_BUTTON_PADDING_NEXT;
      const nextActive = !!nextInfo?.isActive && !!onClick;
      const isDisabled = isAddingToCart || (!showCheckoutAction && !nextActive);

      return (
        <button
          type="button"
          className={`${EXPERIENCE_NEXT_BUTTON_BASE_CLASS} ${paddingClass} ${
            isDisabled ? "booking-cta-pill-disabled tw-cursor-not-allowed" : `${mobileBgColor} tw-cursor-pointer`
          }`}
          onClick={isDisabled ? undefined : (e) => handleNavClick(e, showCheckoutAction ? onCheckout : onClick)}
          disabled={isDisabled}
          style={{ height: MOBILE_ACTION_PILL_HEIGHT }}
        >
          {renderCrossfadedLabel(showCheckoutAction)}
          {withChevron && (
            <span
              className={`${ACTION_CHEVRON_REVEAL_CLASS} ${showVisibleChevron ? "tw-max-w-[32px] tw-opacity-100" : "tw-max-w-0 tw-opacity-0"}`}
              aria-hidden={!showVisibleChevron}
            >
              <MobileActionChevronDown variant="experience" isCheckout={showCheckoutAction} />
            </span>
          )}
        </button>
      );
    };

    const renderCompactActionButton = () => {
      const nextActive = !!nextInfo?.isActive && !!onNextClick;
      const disabled = isAddingToCart || (!showCheckoutAction && !nextActive);
      const compactPadding = showCheckoutAction ? COMPACT_NEXT_PILL_PADDING_CHECKOUT : COMPACT_NEXT_PILL_PADDING_NEXT;

      return (
        <button
          type="button"
          className={`${COMPACT_NEXT_PILL_BASE_CLASS} ${compactPadding} ${disabled ? "booking-cta-pill-disabled tw-cursor-not-allowed" : mobileBgColor}`}
          onClick={disabled ? undefined : (e) => handleNavClick(e, showCheckoutAction ? onCheckout : onNextClick)}
          disabled={disabled}
        >
          {renderCrossfadedLabel(showCheckoutAction)}
          {(showCheckoutAction ? showCheckoutChevron : true) && <MobileActionChevronDown variant="compact" isCheckout={showCheckoutAction} />}
        </button>
      );
    };

    const showMobileNavRow =
      !!previousInfo && !!nextInfo && !!onPreviousClick && !!onNextClick && (showMobileNextButton || (isAnyItemsInCart && showMobileCheckout && !hasPendingSelections));

    const showMobileNavWithCartBar = showMobileNavRow && showChevron;
    const showMobileNavStandalone = showMobileNavRow && !showChevron;

    // Standalone footer (e.g. age-group step): keep the bag toggle on the right while the
    // Next button is gated on selection. Show it by default whenever the cart is not empty
    // (either signal), and hide it entirely when the cart is empty.
    const isCartNotEmpty = cartItemCount > 0 || isAnyItemsInCart;
    const showStandaloneCartToggle = !isPopupHeader && !showChevron && showCartToggleInStandalone && showMobileCartToggle && isCartNotEmpty;

    const renderMobileNavAction = () => {
      if (!showMobileNextButton && !showCheckoutAction) {
        return null;
      }
      if (isExperienceNav) {
        return renderExperienceActionButton(onNextClick, showCheckoutAction ? showCheckoutChevron : true);
      }
      return renderCompactActionButton();
    };

    const showCartTotal = totalAmount > 0;
    const mobileCartToggleButton = (
      <button
        type="button"
        className={`tw-relative tw-flex tw-h-[40px] tw-shrink-0 tw-items-center tw-justify-end tw-gap-[6px] tw-overflow-visible tw-p-0 ${mobileSummaryTransition}`}
        onClick={(e) => handleToggleClick(e, "cart_icon")}
        aria-label={`View cart summary, ${cartItemCount} item${cartItemCount === 1 ? "" : "s"}${
          showCartTotal ? `, total $${formattedTotalAmount}` : ""
        }`}
      >
        {showCartTotal && (
          <span className="tw-text-[12px] tw-font-bold tw-leading-none tw-text-white tw-tabular-nums">${formattedTotalAmount}</span>
        )}
        <MobileCartIcon cartItemCount={cartItemCount} isBattleKart={isBattleKart} />
      </button>
    );

    const renderMobileCartToggleColumn = () => (
      <div className={`tw-relative tw-z-[30] tw-flex tw-shrink-0 tw-items-end tw-overflow-visible ${mobileSummaryTransition}`}>{mobileCartToggleButton}</div>
    );

    /** When expanded, sit flush under the cart pills (no extra top padding). Collapsed keeps the 48px shell for badge alignment. */
    const renderMobileCartBar = () => (
      <div
        ref={alignedRowRef}
        style={alignedRowStyle}
        className={`${MOBILE_FOOTER_ROW_CLASS} ${isTotalVisible ? "tw-min-h-[44px] tw-py-0" : "tw-min-h-[48px] tw-py-[2px]"} ${mobileSummaryTransition}`}
      >
        <div className="tw-flex tw-min-w-0 tw-flex-1">{renderMobileNavAction()}</div>
        {showMobileCartToggle && renderMobileCartToggleColumn()}
      </div>
    );

    return (
      <div
        className={`tw-flex tw-w-full tw-items-center ${
          isPopupHeader ? "tw-justify-end desktop:tw-justify-between" : showChevron ? "tw-justify-between" : "tw-justify-center"
        } ${stickyHeaderClass} desktop:tw-pl-[8px]`}
      >
        {/* Desktop chevron toggle - preserve existing desktop behavior */}
        {showChevron && (
          <div
            className={`tw-hidden desktop:tw-flex tw-text-white tw-text-[14px] tw-font-[700] tw-items-center tw-gap-[4px] tw-cursor-pointer ${
              isTotalVisible ? "tw-bg-transparent" : "desktop:tw-bg-transparent desktop:tw-bg-none"
            } desktop:tw-p-[0px] ${isTotalVisible ? "tw-rounded-br-[10px]" : ""} desktop:tw-rounded-none`}
            onClick={(e) => handleToggleClick(e, "chevron")}
          >
            <MdKeyboardArrowDown
              className={`tw-text-white tw-text-[24px] ${desktopChevronTransition} ${isTotalVisible ? "tw-rotate-0" : "tw-rotate-180"}`}
            />
            <span data-hlc-field={isTotalVisible ? "bottom_summary.hide_label" : "bottom_summary.view_label"}>
              {isTotalVisible ? hideSummaryLabel : viewSummaryLabel}
            </span>
          </div>
        )}

        {/* Age-group step: standalone footer that still shows the cart/bag toggle on the
            right (only when the cart has items). Only the Next button is gated on selection. */}
        {showStandaloneCartToggle && (
          <div ref={alignedRowRef} style={alignedRowStyle} className={`${MOBILE_FOOTER_ROW_CLASS} tw-min-h-[48px] tw-py-[2px] ${mobileSummaryTransition}`}>
            <div className="tw-flex tw-min-w-0 tw-flex-1">{renderMobileNavAction()}</div>
            {renderMobileCartToggleColumn()}
          </div>
        )}

        {/* Mobile nav without cart bar (e.g. experience selector, or age step with empty cart) */}
        {!isPopupHeader && showMobileNavStandalone && !showStandaloneCartToggle && (
          <div
            ref={alignedRowRef}
            style={alignedRowStyle}
            className={`${MOBILE_FOOTER_ROW_CLASS} tw-transition-all tw-duration-[400ms] tw-ease-in-out ${isExperienceNav ? "tw-pt-[4px]" : "tw-py-[2px]"}`}
          >
            {renderMobileNavAction()}
          </div>
        )}

        {showChevron && (
          <>
            {/* Desktop expanded popup header: total on the right */}
            <p
              className={`tw-hidden desktop:tw-block tw-text-[14px] tw-font-semibold tw-text-white tw-bg-transparent tw-py-[4px] tw-px-[4px] ${isTotalVisible ? "desktop:tw-px-[8px] tw-rounded-bl-[10px]" : "desktop:tw-px-[0px]"} desktop:tw-rounded-none`}
            >
              Total ${formattedTotalAmount}
            </p>

            {/* Mobile footer: Next/Checkout fills the row; cart/bag on the right. Always use the
                same cart-bar shell so the bag stays put (no re-mount) while only the Next
                button toggles on selection. */}
            {!isPopupHeader && (showMobileNavWithCartBar || showMobileCartToggle) && renderMobileCartBar()}
          </>
        )}
      </div>
    );
  },
);

SummaryHeader.displayName = "SummaryHeader";
